/**
 * In-memory access token store.
 *
 * The access token lives in JavaScript memory only — never persisted to
 * cookies, localStorage, or sessionStorage. It is lost on page reload by
 * design; reload triggers a silent /v1/auth/refresh call against the
 * httpOnly refresh cookie to restore it.
 *
 * Why in-memory and not persisted:
 *   - XSS within a different module cannot read this closure's bindings
 *     (only module-level exports are reachable).
 *   - Supply-chain compromise running inside the page CAN read it, but only
 *     while the page is open. Token TTL (15 min) bounds the exfiltration
 *     window.
 *   - Browser extensions without `cookies` permission cannot reach the
 *     httpOnly refresh cookie, even with full DOM read access.
 *
 * The refresh cookie is the long-lived secret. JS never touches it.
 *
 * See:
 *   - docs/adr/0009-storage-architecture-and-token-strategy.md
 *   - docs/api-contracts/auth.md
 *   - docs/threat-model.md
 */

let accessToken: string | null = null
let tokenExpiresAt: number | null = null

/**
 * Refresh `SAFETY_MARGIN_MS` before the actual expiry. Prevents requests
 * from racing with token expiry and triggering 401 retry storms.
 */
const SAFETY_MARGIN_MS = 30_000

export const tokenMemory = {
  /**
   * Store a freshly-issued access token.
   * @param token JWT from /v1/auth/login or /v1/auth/refresh response body.
   * @param expiresInSeconds value of `expiresIn` from the response.
   */
  set(token: string, expiresInSeconds: number): void {
    accessToken = token
    tokenExpiresAt = Date.now() + expiresInSeconds * 1000
  },

  /**
   * Return the token if it is present AND not within the refresh safety
   * margin. Returns null otherwise; callers should refresh.
   */
  get(): string | null {
    if (!accessToken || !tokenExpiresAt) return null
    if (Date.now() >= tokenExpiresAt - SAFETY_MARGIN_MS) return null
    return accessToken
  },

  clear(): void {
    accessToken = null
    tokenExpiresAt = null
  },

  isExpired(): boolean {
    return !tokenExpiresAt || Date.now() >= tokenExpiresAt - SAFETY_MARGIN_MS
  },

  /**
   * Diagnostic helper. Never logs the token itself.
   * Intentionally double-underscored so it does not appear as an
   * autocomplete suggestion in normal use.
   */
  __debugInfo(): { hasToken: boolean; expiresInMs: number | null } {
    return {
      hasToken: !!accessToken,
      expiresInMs: tokenExpiresAt ? tokenExpiresAt - Date.now() : null,
    }
  },
} as const
